import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Form } from "react-bootstrap";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
export interface SearchSelectOption {
  value: string;
  label: string;
}

export interface SearchSelectProps {
  id: string;
  label: string;
  value: SearchSelectOption | null;
  options: SearchSelectOption[];
  onChange: (option: SearchSelectOption | null) => void;
  // From useInfiniteSelectOptions: server search and next-page loading.
  onInputChange?: (input: string) => void;
  onMenuScrollToBottom?: () => void;
  isLoading?: boolean;
  // Lets the user add a typed name that isn't in the list (value "").
  isCreatable?: boolean;
  // Shows the X that clears the selection.
  isClearable?: boolean;
  required?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
}

// react-select has no input the browser can validate. A required select
// carries an invisible required input holding its value, so the browser's
// "Please fill out this field" check covers it like the form's other fields.
// It can't be display:none; the browser won't point its message at that.
const VALIDATION_INPUT_STYLE = {
  position: "absolute",
  left: 0,
  bottom: 0,
  width: "100%",
  height: 1,
  opacity: 0,
  pointerEvents: "none",
} as const;

/**
 * The select to use in new forms: a labelled react-select that can search the
 * API (with useInfiniteSelectOptions), create a typed option, clear, and take
 * part in the browser's required-field check. Prefer it over Select2V2 and
 * RequiredReactSelect.
 */
const SearchSelect = ({
  id,
  label,
  value,
  isCreatable = false,
  required = false,
  onChange,
  ...selectProps
}: SearchSelectProps): JSX.Element => {
  const SelectComponent = isCreatable ? CreatableSelect : Select;
  return (
    <Form.Group className="formGroup" style={{ position: "relative" }}>
      <FormFieldLabel className="" label={label} htmlFor={id} />
      <SelectComponent<SearchSelectOption>
        inputId={id}
        value={value}
        onChange={(option) => onChange(option as SearchSelectOption | null)}
        {...(isCreatable && {
          onCreateOption: (name: string) =>
            onChange({ value: "", label: name }),
        })}
        {...selectProps}
      />
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          autoComplete="off"
          required
          value={value?.label ?? ""}
          onChange={() => undefined}
          style={VALIDATION_INPUT_STYLE}
        />
      )}
    </Form.Group>
  );
};

export default SearchSelect;
